import type { CellBorderCss, OoxmlTableCellMeta } from "../ooxml/tableCore";
import { hasAnyBorders } from "../ooxml/tableCore";
import type { OoxmlTableMeta } from "../ooxml/extractTables";
import { flattenOoxmlCellsFromTable, walkHtmlTableCells } from "./tableGrid";
import { applyTableGeometry } from "./tableGeometry";
import { prepareTableForEditorResize } from "./prepareTableForEditor";
import type { TableFidelityStats } from "../types";

const EMPTY_STATS: TableFidelityStats = {
  tablesInDocx: 0,
  tablesInHtml: 0,
  tablesEnriched: 0,
  cellsEnriched: 0,
  bordersApplied: 0,
  mergesApplied: 0,
  widthsApplied: 0,
  alignmentsApplied: 0,
};

function applyBorderStyles(
  el: HTMLElement,
  borders: CellBorderCss | undefined,
  fallback?: CellBorderCss,
): boolean {
  const src = borders && hasAnyBorders(borders) ? borders : fallback;
  if (!src || !hasAnyBorders(src)) return false;

  if (src.top) el.style.borderTop = src.top;
  if (src.right) el.style.borderRight = src.right;
  if (src.bottom) el.style.borderBottom = src.bottom;
  if (src.left) el.style.borderLeft = src.left;
  return true;
}

function applyCellMeta(
  cell: HTMLTableCellElement,
  meta: OoxmlTableCellMeta,
  tableBorders: CellBorderCss | undefined,
): {
  enriched: boolean;
  borders: boolean;
  merges: boolean;
  widths: boolean;
  alignments: boolean;
} {
  const result = {
    enriched: false,
    borders: false,
    merges: false,
    widths: false,
    alignments: false,
  };

  if (meta.colSpan > 1 && cell.colSpan !== meta.colSpan) {
    cell.colSpan = meta.colSpan;
    result.merges = true;
    result.enriched = true;
  }
  if (meta.rowSpan > 1 && cell.rowSpan !== meta.rowSpan) {
    cell.rowSpan = meta.rowSpan;
    result.merges = true;
    result.enriched = true;
  }

  // Column widths are applied via colwidth/colgroup in applyTableGeometry — not inline
  // cell.style.width, which blocks TipTap column resize after import.

  if (meta.textAlign && !cell.style.textAlign) {
    cell.style.textAlign = meta.textAlign;
    result.alignments = true;
    result.enriched = true;
  }

  if (meta.verticalAlign) {
    cell.style.verticalAlign = meta.verticalAlign;
    result.alignments = true;
    result.enriched = true;
  }

  if (meta.backgroundColor && !cell.style.backgroundColor) {
    cell.style.backgroundColor = meta.backgroundColor;
    result.enriched = true;
  }

  if (applyBorderStyles(cell, meta.borders, tableBorders)) {
    result.borders = true;
    result.enriched = true;
  }

  if (meta.isHeader && cell.tagName === "TD") {
    const th = cell.ownerDocument.createElement("th");
    th.innerHTML = cell.innerHTML;
    for (const attr of Array.from(cell.attributes)) {
      th.setAttribute(attr.name, attr.value);
    }
    th.style.cssText = cell.style.cssText;
    cell.parentElement?.replaceChild(th, cell);
    result.enriched = true;
  }

  return result;
}

function applyTableMeta(
  table: HTMLTableElement,
  meta: OoxmlTableMeta,
): Omit<TableFidelityStats, "tablesInDocx" | "tablesInHtml"> {
  const stats = {
    tablesEnriched: 0,
    cellsEnriched: 0,
    bordersApplied: 0,
    mergesApplied: 0,
    widthsApplied: 0,
    alignmentsApplied: 0,
  };

  table.classList.add("lexgat-import-table");
  table.style.borderCollapse = "collapse";

  if (meta.widthPercent != null) {
    table.style.width = `${meta.widthPercent}%`;
    stats.tablesEnriched++;
  } else if (meta.widthPx != null) {
    table.style.width = `${meta.widthPx}px`;
    stats.tablesEnriched++;
  }

  const htmlCells = walkHtmlTableCells(table);
  const ooxmlCells = flattenOoxmlCellsFromTable(meta);

  const limit = Math.min(htmlCells.length, ooxmlCells.length);
  for (let i = 0; i < limit; i++) {
    const applied = applyCellMeta(
      htmlCells[i].item,
      ooxmlCells[i],
      meta.borders,
    );
    if (applied.enriched) stats.cellsEnriched++;
    if (applied.borders) stats.bordersApplied++;
    if (applied.merges) stats.mergesApplied++;
    if (applied.widths) stats.widthsApplied++;
    if (applied.alignments) stats.alignmentsApplied++;
  }

  const geometry = applyTableGeometry(table, meta);
  if (geometry.columnWidthsApplied > 0) {
    stats.widthsApplied += geometry.columnWidthsApplied;
  }

  const htmlRows = Array.from(table.querySelectorAll("tr"));
  for (let i = 0; i < Math.min(htmlRows.length, meta.rows.length); i++) {
    const rowMeta = meta.rows[i];
    if (rowMeta.heightPx == null || rowMeta.heightPx <= 0) continue;
    const tr = htmlRows[i];
    if (!(tr instanceof HTMLTableRowElement)) continue;
    if (rowMeta.heightRule === "atLeast") {
      tr.style.minHeight = `${rowMeta.heightPx}px`;
    } else {
      tr.style.height = `${rowMeta.heightPx}px`;
    }
  }

  prepareTableForEditorResize(table);

  if (stats.cellsEnriched > 0 || stats.tablesEnriched > 0 || geometry.columnWidthsApplied > 0) {
    stats.tablesEnriched++;
  }

  return stats;
}

/**
 * Apply OOXML table metadata to mammoth HTML tables (Phase 3).
 */
export function applyTableMetaToHtml(
  html: string,
  metaList: OoxmlTableMeta[],
): { html: string; stats: TableFidelityStats } {
  const stats: TableFidelityStats = { ...EMPTY_STATS };
  stats.tablesInDocx = metaList.length;

  if (!html.trim() || metaList.length === 0) {
    return { html, stats };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<div id="tbl-root">${html}</div>`,
    "text/html",
  );
  const root = doc.getElementById("tbl-root");
  if (!root) return { html, stats };

  const htmlTables = Array.from(root.querySelectorAll("table"));
  stats.tablesInHtml = htmlTables.length;

  const limit = Math.min(htmlTables.length, metaList.length);
  for (let i = 0; i < limit; i++) {
    const tableStats = applyTableMeta(htmlTables[i], metaList[i]);
    stats.tablesEnriched += tableStats.tablesEnriched > 0 ? 1 : 0;
    stats.cellsEnriched += tableStats.cellsEnriched;
    stats.bordersApplied += tableStats.bordersApplied;
    stats.mergesApplied += tableStats.mergesApplied;
    stats.widthsApplied += tableStats.widthsApplied;
    stats.alignmentsApplied += tableStats.alignmentsApplied;
  }

  return { html: root.innerHTML.trim(), stats };
}
